import { useEffect, useRef } from "react";

const IDLE_TIMEOUT_MINUTES = 20;
const HEARTBEAT_INTERVAL_MINUTES = 5;
const UTM_EXPIRY_MINUTES = 20;

function getOktaId() {
  // Try injected headers first
  if (typeof window.__HEADERS__ === "object" && window.__HEADERS__.oktaId) {
    return window.__HEADERS__.oktaId;
  }
  // Fallback to cookie
  const match = document.cookie.match(/(^|;\s*)oktaId=([^;]*)/);
  if (match) return decodeURIComponent(match[2]);
  return "";
}

function getOrResetSessionIdAndUtmParams() {
  const now = new Date();
  const injectedHeaders =
    typeof window.__HEADERS__ === "object" ? window.__HEADERS__ : {};
  const searchParams = new URLSearchParams(window.location.search);

  // --- Use localStorage for session ID persistence ---
  let sessionId = localStorage.getItem("session_id");
  let sessionCreatedAt = localStorage.getItem("session_created_at");
  let isExpired = true;

  if (sessionId && sessionCreatedAt) {
    const createdAt = new Date(sessionCreatedAt);
    isExpired = now - createdAt > UTM_EXPIRY_MINUTES * 60 * 1000;
  }

  if (!sessionId || !sessionCreatedAt || isExpired) {
    sessionId = crypto.randomUUID();
    sessionCreatedAt = now.toISOString();
    localStorage.setItem("session_id", sessionId);
    localStorage.setItem("session_created_at", sessionCreatedAt);
  }
  // --- End localStorage logic ---

  const utmParams = {
    utm_source:
      injectedHeaders.utmSource || searchParams.get("utm_source") || "",
    utm_medium:
      injectedHeaders.utmMedium || searchParams.get("utm_medium") || "",
    utm_campaign:
      injectedHeaders.utmCampaign || searchParams.get("utm_campaign") || "",
    utm_content:
      injectedHeaders.utmContent || searchParams.get("utm_content") || "",
    utm_term: injectedHeaders.utmTerm || searchParams.get("utm_term") || "",
  };

  const oktaId = getOktaId();

  return { sessionId, utmParams, oktaId };
}

const SessionManagement = ({
  organisation,
  destinationGroup,
  restServicePost,
}) => {
  const sessionId = useRef(null);
  const utmParams = useRef(null);
  const oktaId = useRef(null);
  const sessionStartTime = useRef(new Date().toISOString());
  const sessionEnded = useRef(false);
  const idleTimeout = useRef(null);
  const heartbeatInterval = useRef(null);

  const sendSessionEnd = (reason = "unload") => {
    if (sessionEnded.current) return;
    sessionEnded.current = true;

    const payload = {
      session_id: sessionId.current,
      organisation_id: organisation.id,
      destination_group_id: destinationGroup.id,
      session_end: new Date().toISOString(),
      end_reason: reason,
      okta_id: oktaId.current,
    };

    navigator.sendBeacon(
      process.env.REACT_APP_API_BASE_URL + "addSessionInformation",
      new Blob([JSON.stringify(payload)], { type: "application/json" })
    );
  };

  const sendHeartbeat = () => {
    const payload = {
      session_id: sessionId.current,
      timestamp: new Date().toISOString(),
    };
    restServicePost(this.props.apiToken, "sessionHeartbeat", payload)
      .then(() => console.log("Heartbeat sent"))
      .catch((err) => console.error("Heartbeat failed", err));
  };

  const resetIdleTimer = () => {
    clearTimeout(idleTimeout.current);
    idleTimeout.current = setTimeout(() => {
      console.log("⏳ Idle timeout, ending session");
      sendSessionEnd("idle_timeout");
    }, IDLE_TIMEOUT_MINUTES * 60 * 1000);
  };

  useEffect(() => {
    if (!organisation?.id || !destinationGroup?.id) return;

    sessionEnded.current = false;

    let countdown = 5;
    const countdownInterval = setInterval(() => {
      console.log(`SessionManagement: API call in ${countdown} second(s)...`);
      countdown -= 1;
      if (countdown === 0) clearInterval(countdownInterval);
    }, 1000);

    const timeout = setTimeout(() => {
      const sessionInfo = getOrResetSessionIdAndUtmParams();
      sessionId.current = sessionInfo.sessionId;
      utmParams.current = sessionInfo.utmParams;
      oktaId.current = sessionInfo.oktaId;

      const payload = {
        session_id: sessionId.current,
        organisation_id: organisation.id,
        destination_group_id: destinationGroup.id,
        club_code: "NYM",
        venue_id: "3289",
        session_start: sessionStartTime.current,
        okta_id: oktaId.current,
        campaign_id: utmParams.current.utm_campaign,
        campaign_source: utmParams.current.utm_source,
        campaign_medium: utmParams.current.utm_medium,
        campaign_name: utmParams.current.utm_content,
        campaign_term: utmParams.current.utm_term,
      };

      restServicePost(this.props.apiToken, "addSessionInformation", payload)
        .then(() => console.log("Session start logged:", sessionId.current))
        .catch((error) => console.error("Session logging failed", error));

      const unloadHandler = () => sendSessionEnd("unload");
      const visibilityHandler = () => {
        if (document.visibilityState === "hidden") {
          sendSessionEnd("visibility_hidden");
        }
      };

      const activityEvents = [
        "mousemove",
        "mousedown",
        "keydown",
        "scroll",
        "touchstart",
      ];
      activityEvents.forEach((e) => window.addEventListener(e, resetIdleTimer));
      window.addEventListener("beforeunload", unloadHandler);
      window.addEventListener("unload", unloadHandler);
      document.addEventListener("visibilitychange", visibilityHandler);
      resetIdleTimer();

      sendHeartbeat();
      heartbeatInterval.current = setInterval(
        sendHeartbeat,
        HEARTBEAT_INTERVAL_MINUTES * 60 * 1000
      );
    }, 5000); // Wait 5 seconds before making the API call

    return () => {
      clearTimeout(timeout);
      clearInterval(countdownInterval);
      clearTimeout(idleTimeout.current);
      clearInterval(heartbeatInterval.current);
      window.removeEventListener("beforeunload", sendSessionEnd);
      window.removeEventListener("unload", sendSessionEnd);
      document.removeEventListener("visibilitychange", sendSessionEnd);
      ["mousemove", "mousedown", "keydown", "scroll", "touchstart"].forEach(
        (e) => window.removeEventListener(e, resetIdleTimer)
      );
    };
  }, [organisation, destinationGroup, restServicePost]);

  return null;
};

export default SessionManagement;
